import type ServiceApi from "../api";
import {isCurrentEnvironmentInDevelopMode} from "../environment";
import {ErrorI18n} from "../i18n";
import {getXErrorData, type QueryError} from "../util/errorUtil";
import type {ApiCredentials} from "./credentials";
import type {ApiExecutor} from "./executor";
import {isAbortError, isTimeoutError} from "./executor";
import {type HandleValidationError, ValidationErrorI18n} from "./validation";
import {WebError} from "./web-error";

/**
 * General API Interface.
 */
export abstract class ApiInterface<Response, ExecArgs extends any[]> {
    /**
     * Execute the API request.
     * @param api injected API for execution
     * @param args additional arguments to the execution
     */
    abstract execute(api: ServiceApi, ...args: ExecArgs): Promise<Response>;

    /**
     * Validate input data, and dispatch error if not successful.
     * @param handleError error response function
     * @return true if validate was successful
     */
    validateAndDispatchError(handleError: HandleValidationError): boolean {
        try {
            this.validate();
            return true;
        } catch (e) {
            if (e instanceof ValidationErrorI18n) {
                handleError(e.message, e.messageArgs, e.failedFields());
            } else if (e instanceof ErrorI18n) {
                handleError(e.message, e.messageArgs, []);
            } else {
                console.log(`Unexpected error during validation of ${this}`, e);
                handleError("general.error.unspecific", null, []);
            }
        }
        return false;
    }

    /**
     * Validate input data, and dispatch a ready-to-use {@link QueryError} if not successful.
     * Wraps {@link validateAndDispatchError} and converts the reported validation error into a
     * QueryError; only the unspecific-error fallback gets a contact link.
     * @param handleQueryError receives the QueryError to dispatch (e.g. into a setXxxError reducer)
     * @return true if validate was successful
     */
    validateAndDispatchQueryError(handleQueryError: (queryError: QueryError) => void): boolean {
        return this.validateAndDispatchError((error, errorArgs) =>
            handleQueryError({
                error,
                errorArgs: errorArgs ?? undefined,
                errorHasContactLink: error === "general.error.unspecific",
            }),
        );
    }

    /**
     * Method to create the URL for the request. Use the injected API to resolve API paths.
     * Has to be overridden in implementations.
     * @param api injected API for execution
     */
    protected abstract buildUrl(api: ServiceApi): string;

    /**
     * Method to create the Payload for the request.
     * Can to be overridden in implementations, if the request method supports payloads.
     */
    protected buildPayload(): any {
        return null;
    }

    /**
     * Method to validate parameters.
     * Can to be overridden in responses, if there are parameters to validate.
     * @protected
     */
    protected validate(): void {}

    /**
     * Handles response errors.
     * @param e caught error from response evaluation
     * @return if the error can be evaluated to a valid response for this API interface, return the evaluated response.
     */
    protected handleApiErrors(e: Error): Response {
        // TODO: Refactor error handling to a more stream-lines approach
        if (isTimeoutError(e)) {
            throw new ErrorI18n("general.error.timeout");
        }
        if (e instanceof WebError) {
            if (e.getErrorCode()) {
                throw new ErrorI18n("general.error.custom", {
                    errorData: e.getCustomErrorData(),
                });
            }

            switch (e.status) {
                case 204:
                    throw new ErrorI18n("general.error.custom", {errorData: getXErrorData(e)});
                case 401:
                    throw new ErrorI18n("general.error.401");
                case 403:
                    throw new ErrorI18n("general.error.403");
                case 404:
                    throw new ErrorI18n("general.error.404");
                case 409:
                    throw new ErrorI18n("address.error.409");
                case 422:
                    throw new ErrorI18n("address.error.422");
                case 503:
                    throw new ErrorI18n("general.error.503");
                case 500:
                    throw new ErrorI18n("general.error.unspecific");
                case 408: /* Request Timeout */
                case 504 /* Gateway Timeout */:
                    throw new ErrorI18n("general.error.timeout");
                default:
            }
        }
        throw new ErrorI18n("general.error.invalid");
    }
}

export abstract class SimpleApiInterface<Response> extends ApiInterface<Response, [ApiCredentials]> {
    async execute(api: ServiceApi, credentials: ApiCredentials): Promise<Response> {
        let url: string = "uninitialized-url";
        try {
            url = this.buildUrl(api);
            const response = await this.executeInternal(api.executor, url, credentials);
            this.logSuccessfulResponse(url);
            return response;
        } catch (e) {
            this.logFailedResponse(url, e);
            // Eine abgebrochene Anfrage (Live-Freitextfilter, von einem neueren Tastendruck überholt) ist
            // erwartbar: unverändert weiterwerfen, damit der aufrufende Thunk sie schlucken kann, statt
            // dass handleApiErrors daraus einen nutzersichtbaren "invalid response"-Fehler macht.
            if (isAbortError(e)) {
                throw e;
            }
            // Ein DOMException ist nicht in jeder Umgebung ein Error (jsdom), wird aber am Namen erkannt.
            if (e instanceof Error || isTimeoutError(e)) {
                return this.handleApiErrors(e as Error);
            }
            console.error(`Unexpected Error type '${typeof e}': ${e}`);
            return this.handleApiErrors(new Error("Unexpected error"));
        }
    }

    /**
     * Log a successful response. Only logs in develop mode. Override to silence overly verbose
     * interfaces (e.g. map tiles, which issue one request per tile).
     * @param url the resolved request URL
     */
    protected logSuccessfulResponse(url: string): void {
        if (isCurrentEnvironmentInDevelopMode()) {
            console.log(`Successful API response from: ${url}`);
        }
    }

    /**
     * Log a failed response. Only logs in develop mode. Override to silence overly verbose interfaces.
     * @param url the resolved request URL (or the placeholder if buildUrl() threw)
     * @param error the caught error
     */
    protected logFailedResponse(url: string, error: unknown): void {
        if (isCurrentEnvironmentInDevelopMode()) {
            console.log(`Failed API response from: ${url}`, error);
        }
    }

    protected abstract executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<Response>;
}
